import { betterAjvErrors } from '@apideck/better-ajv-errors';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import { Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Shadows, ThemeOptions, ThemeProvider, Typography, createTheme } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Form from '@rjsf/mui';
import { FieldTemplateProps, ObjectFieldTemplateProps, RegistryWidgetsType } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { ApiModuleData } from '@shared/interfaces/ApiInterfaces';
import Ajv, { ErrorObject } from "ajv";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MultiError } from 'utils/errors';
import { getAccessToken } from 'utils/accessTokenStore';
import { buildFormDataFromQueryParams } from 'utils/queryParamFormData';
import { isAdmin, renderWithEval } from 'utils/utils';
import BackendApiService from "../services/BackendApiService";
import CancelRequestDialog from './CancelRequestDialog';
import CustomDate from './CustomDate';
import CustomInputWidget from './CustomInputWidget';
import CustomSelectWidget from './CustomSelectWidget';
import DeleteResourceDialog from './DeleteResourceDialog';
import FormAutocomplete from "./FormAutocomplete";
import { MarkdownText } from './MarkdownText';
import RequestStatusChip from './RequestStatusChip';
import CustomArrayFieldTemplate from './templates/CustomArrayFieldTemplate';
import CustomArrayFieldItemTemplate from './templates/CustomArrayFieldItemTemplate';

/**
 * Inline DescriptionFieldTemplate with Markdown support
 */
const hiddenDescriptionSpacing = "__hidden_description_spacing__";

const DescriptionFieldTemplate = (props: any) => {
  const { id, description } = props;

  const descriptionStyle = {
    margin: '0',
    padding: '0.2rem 0.45rem 0.3rem',
    fontSize: '0.9rem',
    color: 'rgba(0, 0, 0, 0.6)',
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    fontWeight: 400,
    lineHeight: 1.66
  } as const;
  
  if (!description) {
    return null;
  }

  // Root description is already rendered in the custom top info box.
  if (id === 'root__description') {
    return null;
  }

  if (description === hiddenDescriptionSpacing) {
    return <div id={id} style={{ ...descriptionStyle, fontSize: '0.3rem', visibility: 'hidden' }}>placeholder</div>;
  }
  
  // If description is already a ReactElement, render it as-is
  if (typeof description !== 'string') {
    return <div id={id} style={descriptionStyle}>{description}</div>;
  }
  
  return (
    <div id={id} style={descriptionStyle}>
      <MarkdownText>{description}</MarkdownText>
    </div>
  );
};

const FieldTemplate = (props: FieldTemplateProps<any, any, any>) => {
  const { hidden, classNames, style, children, description, schema } = props;

  if (hidden) {
    return <div style={{ display: 'none' }}>{children}</div>;
  }

  const isArrayField = schema?.type === 'array';
  const isObjectField = schema?.type === 'object';

  return (
    <div className={classNames} style={{ ...style, marginBottom: '0.45rem' }}>
      {children}
      {!isArrayField && !isObjectField && description}
    </div>
  );
};

/**
 * Recursively removes undefined properties and invalid schema constructs from schema to prevent RJSF errors
 * This handles cases where backend parsing creates schema properties with undefined values or empty arrays
 */
function removeUndefinedFromSchema(obj: any, path: string = 'root'): any {
  if (obj === undefined || obj === null) {
    console.warn(`[Schema Cleanup] Found undefined/null at: ${path}`);
    return undefined;
  }
  
  if (Array.isArray(obj)) {
    return obj.map((item, index) => removeUndefinedFromSchema(item, `${path}[${index}]`)).filter(item => item !== undefined);
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value !== undefined) {
          const cleanedValue = removeUndefinedFromSchema(value, `${path}.${key}`);
          
          // Skip empty arrays for JSON Schema validation keywords (must have at least 1 item)
          if (Array.isArray(cleanedValue) && cleanedValue.length === 0) {
            if (['anyOf', 'oneOf', 'allOf', 'enum'].includes(key)) {
              console.warn(`[Schema Cleanup] Removed empty ${key} array at: ${path}.${key}`);
              continue;
            }
          }
          
          if (cleanedValue !== undefined) {
            cleaned[key] = cleanedValue;
          }
        } else {
          console.warn(`[Schema Cleanup] Removed undefined at: ${path}.${key}`);
        }
      }
    }
    return cleaned;
  }
  
  return obj;
}

const getPropsForGroup = (
  group: any,
  props: ObjectFieldTemplateProps
): ObjectFieldTemplateProps => {
  return {
    ...props,
    properties: props.properties.filter((p) => group.fields.includes(p.name))
  };
};

const isValueProvided = (value: any): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }

  return true;
};

const getRootErrorField = (property: string | undefined): string | null => {
  if (!property) {
    return null;
  }

  const normalized = property.replace(/^\.+|\.+$/g, '');
  if (!normalized) {
    return null;
  }

  const firstSegment = normalized.split('.')[0];
  if (!firstSegment || /^\d+$/.test(firstSegment)) {
    return null;
  }

  return firstSegment;
};

const findGroupIndexForField = (groups: any[], field: string | null): number | null => {
  if (!field || !Array.isArray(groups)) {
    return null;
  }

  const groupIndex = groups.findIndex((group: any) => Array.isArray(group?.fields) && group.fields.includes(field));
  return groupIndex >= 0 ? groupIndex : null;
};

const extractRequestIdFromStatus = (statusResponse: any): string | undefined => {
  const metadata = statusResponse?.metaData;
  const candidates = [
    metadata?.azureDevOps?.prId,
    metadata?.pullRequestId,
    metadata?.requestId,
    metadata?.id,
    metadata?.pullrequestId,
    statusResponse?.pullRequestId,
    statusResponse?.requestId,
    statusResponse?.id
  ];

  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && String(candidate).trim() !== '') {
      return String(candidate);
    }
  }

  const message = typeof statusResponse?.message === 'string' ? statusResponse.message : '';
  const numericIdFromMessage = message.match(/#(\d+)/)?.[1] || message.match(/\b(\d{2,})\b/)?.[1];
  return numericIdFromMessage;
};

const collectSchemaProperties = (schema: any): Record<string, any> => {
  if (!schema || typeof schema !== 'object') return {};

  const collected: Record<string, any> = {};

  if (schema.properties) {
    Object.entries(schema.properties).forEach(([key, value]) => {
      collected[key] = value;
    });
  }

  const compositionKeywords = ['allOf', 'anyOf', 'oneOf'];
  for (const keyword of compositionKeywords) {
    if (Array.isArray(schema[keyword])) {
      for (const subSchema of schema[keyword]) {
        Object.assign(collected, collectSchemaProperties(subSchema));
      }
    }
  }

  for (const condKeyword of ['then', 'else']) {
    if (schema[condKeyword]) {
      Object.assign(collected, collectSchemaProperties(schema[condKeyword]));
    }
  }

  return collected;
};

const normalizeFormDataForSchema = (data: any, schema: any): any => {
  if (data === undefined) {
    return undefined;
  }

  if (data === null || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    const itemSchema = schema?.items;
    return data.map((entry) => normalizeFormDataForSchema(entry, itemSchema));
  }

  const schemaProperties = collectSchemaProperties(schema);
  const hasSchemaProperties = Object.keys(schemaProperties).length > 0;
  const keys = hasSchemaProperties
    ? Object.keys(data).filter((key) => data[key] !== undefined && key in schemaProperties)
    : Object.keys(data).filter((key) => data[key] !== undefined);

  const normalized: Record<string, any> = {};
  keys.sort().forEach((key) => {
    const normalizedValue = normalizeFormDataForSchema(data[key], schemaProperties[key]);
    if (normalizedValue !== undefined) {
      normalized[key] = normalizedValue;
    }
  });

  return normalized;
};

const formatFieldLabel = (fieldName: string): string => {
  return fieldName
    .split('.')
    .filter((segment) => !/^\d+$/.test(segment))
    .join('.')
    .replace(/_/g, ' ')
    .trim();
};

const getFriendlyValidationMessage = (error: any): string => {
  const property = typeof error?.property === 'string' ? error.property : '';
  const rawMessage = typeof error?.message === 'string'
    ? error.message
    : (typeof error?.stack === 'string' ? error.stack : 'Invalid value');

  const cleanProperty = property.replace(/^\.+|\.+$/g, '');
  const fieldLabel = cleanProperty ? formatFieldLabel(cleanProperty) : '';

  const requiredMatch = rawMessage.match(/required property '([^']+)'/);
  if (requiredMatch?.[1]) {
    return `${formatFieldLabel(requiredMatch[1])} is required.`;
  }

  const normalizedMessage = rawMessage
    .replace(/must match a schema in anyOf/gi, 'must be selected')
    .replace(/must be equal to one of the allowed values/gi, 'contains an invalid value');

  if (!fieldLabel) {
    return normalizedMessage;
  }

  if (normalizedMessage.toLowerCase().includes(fieldLabel.toLowerCase())) {
    return normalizedMessage;
  }

  return `${fieldLabel}: ${normalizedMessage}`;
};

const focusFormField = (property: string | undefined) => {
  if (!property) {
    return;
  }

  const normalizedProperty = property.replace(/^\.+|\.+$/g, '');
  if (!normalizedProperty) {
    return;
  }

  const focusElement = (element: HTMLElement | null) => {
    if (!element) {
      return false;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.focus();
    return true;
  };

  const byName = document.getElementsByName(normalizedProperty)?.[0] as HTMLElement | undefined;
  if (focusElement(byName || null)) {
    return;
  }

  const normalizedId = normalizedProperty.replace(/\./g, '_');
  const fieldContainer = document.getElementById(`root_${normalizedId}`) as HTMLElement | null;

  if (fieldContainer) {
    const nestedFocusable = fieldContainer.querySelector('input, textarea, [role="combobox"], button') as HTMLElement | null;
    if (focusElement(nestedFocusable) || focusElement(fieldContainer)) {
      return;
    }
  }
};

const uiTokens = {
  borderColor: '#d8dee6',
  surface: '#ffffff',
  surfaceMuted: '#f7fafc',
  accent: '#0f6cbd',
  shadow: '0 10px 28px rgba(20, 45, 70, 0.08)',
  radius: {
    md: 2,
    lg: 3
  }
};

const isAdvancedField = (element: any): boolean => {
  return Boolean(
    element?.content?.props?.uiSchema?.["ui:advanced"] ||
    element?.content?.props?.uiSchema?.["ui:options"]?.advanced
  );
};

const isHiddenField = (element: any): boolean => {
  return Boolean(
    element?.hidden ||
    element?.content?.props?.hidden ||
    element?.content?.props?.uiSchema?.['ui:widget'] === 'hidden'
  );
};

const isExplicitlyHiddenField = (element: any): boolean => {
  return element?.content?.props?.uiSchema?.['ui:widget'] === 'hidden';
};

const isDeferredSelectLikeField = (element: any): boolean => {
  if (isExplicitlyHiddenField(element)) {
    return false;
  }

  const isCurrentlyHidden = Boolean(element?.hidden || element?.content?.props?.hidden);
  if (!isCurrentlyHidden) {
    return false;
  }

  const schema = element?.content?.props?.schema || {};
  const uiSchema = element?.content?.props?.uiSchema || {};
  const widget = String(uiSchema?.['ui:widget'] || '').toLowerCase();
  const hasSelectableValues =
    Array.isArray(schema?.enum) ||
    Array.isArray(schema?.oneOf) ||
    Array.isArray(schema?.anyOf);

  return hasSelectableValues || widget.includes('select') || widget.includes('autocomplete');
};

const shouldShowDeferredPlaceholderForField = (rootSchema: any, rootUiSchema: any, fieldName: string, showAdvancedFields: boolean): boolean => {
  const fieldSchema = rootSchema?.properties?.[fieldName];
  if (!fieldSchema) {
    return false;
  }

  const fieldUiSchema = rootUiSchema?.[fieldName] || {};
  const isAdvanced = Boolean(fieldUiSchema?.['ui:advanced'] || fieldUiSchema?.['ui:options']?.advanced);
  if (!showAdvancedFields && isAdvanced) {
    return false;
  }

  if (fieldUiSchema?.['ui:widget'] === 'hidden') {
    return false;
  }

  const widget = String(fieldUiSchema?.['ui:widget'] || '').toLowerCase();
  const hasSelectableValues =
    Array.isArray(fieldSchema?.enum) ||
    Array.isArray(fieldSchema?.oneOf) ||
    Array.isArray(fieldSchema?.anyOf);

  return hasSelectableValues || widget.includes('select') || widget.includes('autocomplete');
};

const getVisibleProperties = (properties: any[], showAdvancedFields: boolean): any[] => {
  const nonHiddenProperties = properties.filter((element: any) => !isHiddenField(element) || isDeferredSelectLikeField(element));

  if (showAdvancedFields) {
    return nonHiddenProperties;
  }

  return nonHiddenProperties.filter((element: any) => !isAdvancedField(element));
};

const getAutoColumn = (element: any, defaultColumn: string): string => {
  const uiSchema = element?.content?.props?.uiSchema || {};
  const schema = element?.content?.props?.schema || {};
  const explicitColumn = uiSchema?.["ui:column"];
  const widget = String(uiSchema?.["ui:widget"] || '').toLowerCase();
  const rows = Number(uiSchema?.["ui:options"]?.rows || 0);
  const fieldType = schema?.type;

  const isComplexField =
    fieldType === 'object' ||
    fieldType === 'array' ||
    widget === 'textarea' ||
    rows > 1;

  // For nested object templates (defaultColumn === "6"), keep simple fields side-by-side
  // even if their ui schema defaults them to 12 columns.
  if (defaultColumn === "6" && !isComplexField && String(explicitColumn || '') === '12') {
    return '6';
  }

  return String(explicitColumn ?? defaultColumn);
};

const renderField = (properties: any[], defaultColumn: string = "12") => properties.map((element: any, index: number) => {
  let column: string = getAutoColumn(element, defaultColumn);
  let newLine = false;

  // n -> indicator to force new line
  if (column.startsWith("n")) {
    column = column.replace("n", "");
    newLine = true;
  }

  const parsedColumn = Number(column);
  const safeColumn = Number.isFinite(parsedColumn) && parsedColumn > 0 ? Math.min(parsedColumn, 12) : 12;

  if (element?.__deferredPlaceholder) {
    const title = element?.title || element?.name || 'Selection';

    let placeholderRet = [
      <Grid
        key={`form-grid-missing-deferred-${index}`}
        item xs={12} md={safeColumn}>
        <Box sx={{ opacity: 0.78 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.4 }}>
            {title}
          </Typography>
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
              minHeight: 40,
              px: 1.4,
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              backgroundColor: 'action.disabledBackground'
            }}
          >
            Please complete the prerequisite selection first.
          </Box>
        </Box>
      </Grid>
    ];

    if (newLine) {
      placeholderRet = [<Box key={`form-grid-missing-deferred-box-${index}`} width="100%" />].concat(placeholderRet);
    }

    return placeholderRet;
  }

  if (isDeferredSelectLikeField(element)) {
    const schema = element?.content?.props?.schema || {};
    const title = schema?.title || element?.name || 'Selection';

    let deferredRet = [
      <Grid
        key={`form-grid-deferred-${index}`}
        item xs={12} md={safeColumn}>
        <Box sx={{ opacity: 0.78 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.4 }}>
            {title}
          </Typography>
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 1,
              minHeight: 40,
              px: 1.4,
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              backgroundColor: 'action.disabledBackground'
            }}
          >
            Please complete the prerequisite selection first.
          </Box>
        </Box>
      </Grid>
    ];

    if (newLine) {
      deferredRet = [<Box key={`form-grid-deferred-box-${index}`} width="100%" />].concat(deferredRet);
    }

    return deferredRet;
  }

  if (isHiddenField(element)) {
    return null;
  }

  let ret = [
    <Grid
      key={`form-grid-${index}`}
      item xs={12} md={safeColumn}>
      {element.content}
    </Grid>
  ];

  if (newLine) {
    ret = [<Box key={`form-grid-box-${index}`} width="100%" />].concat(ret);
  }

  return ret;
}
);

const applyCustomArrayTemplatesRecursively = (schemaNode: any, uiSchemaNode: any) => {
  if (!schemaNode || !uiSchemaNode || typeof schemaNode !== 'object' || typeof uiSchemaNode !== 'object') {
    return;
  }

  if (schemaNode.type === 'array') {
    if (!uiSchemaNode["ui:ArrayFieldTemplate"]) {
      uiSchemaNode["ui:ArrayFieldTemplate"] = CustomArrayFieldTemplate;
    }
    if (!uiSchemaNode["ui:ArrayFieldItemTemplate"]) {
      uiSchemaNode["ui:ArrayFieldItemTemplate"] = CustomArrayFieldItemTemplate;
    }

    const itemSchema = schemaNode.items;
    if (Array.isArray(itemSchema)) {
      if (!Array.isArray(uiSchemaNode.items)) {
        uiSchemaNode.items = [];
      }

      itemSchema.forEach((entry, index) => {
        if (!uiSchemaNode.items[index] || typeof uiSchemaNode.items[index] !== 'object' || Array.isArray(uiSchemaNode.items[index])) {
          uiSchemaNode.items[index] = {};
        }
        applyCustomArrayTemplatesRecursively(entry, uiSchemaNode.items[index]);
      });
    } else if (itemSchema) {
      if (!uiSchemaNode.items || typeof uiSchemaNode.items !== 'object' || Array.isArray(uiSchemaNode.items)) {
        uiSchemaNode.items = {};
      }
      applyCustomArrayTemplatesRecursively(itemSchema, uiSchemaNode.items);
    }
  }

  if (schemaNode.type === 'object') {
    const properties = schemaNode.properties || {};
    Object.keys(properties).forEach((propertyName) => {
      if (!uiSchemaNode[propertyName] || typeof uiSchemaNode[propertyName] !== 'object' || Array.isArray(uiSchemaNode[propertyName])) {
        uiSchemaNode[propertyName] = {};
      }
      applyCustomArrayTemplatesRecursively(properties[propertyName], uiSchemaNode[propertyName]);
    });

    if (schemaNode.additionalProperties) {
      if (!uiSchemaNode.additionalProperties || typeof uiSchemaNode.additionalProperties !== 'object' || Array.isArray(uiSchemaNode.additionalProperties)) {
        uiSchemaNode.additionalProperties = {};
      }
      applyCustomArrayTemplatesRecursively(schemaNode.additionalProperties, uiSchemaNode.additionalProperties);
    }
  }
};


function ObjectFieldTemplate(props: any) {
  const [expanded, setExpanded] = useState<string | false>('panel0');
  const [showDescriptionDetails, setShowDescriptionDetails] = useState<boolean>(false);
  const [showAdvancedFields, setShowAdvancedFields] = useState<boolean>(false);
  const versionFallback = props.uiSchema["ui:versionFallback"];
  const groups = Array.isArray(props.uiSchema?.groups) ? props.uiSchema.groups : [];
  const forcedExpandedGroup = props.formContext?.__expandedGroup;

  useEffect(() => {
    if (Number.isInteger(forcedExpandedGroup) && forcedExpandedGroup >= 0) {
      setExpanded(`panel${forcedExpandedGroup}`);
    }
  }, [forcedExpandedGroup]);

  const descriptionLines = useMemo(() => {
    if (!props.description) {
      return [];
    }

    return String(props.description)
      .split("\\n")
      .map((item: string) => item.trim())
      .filter((item: string) => item.length > 0);
  }, [props.description]);

  const hasAdvancedFields = useMemo(() => {
    return props.properties?.some((property: any) => isAdvancedField(property)) || false;
  }, [props.properties]);

  const hiddenAdvancedCount = useMemo(() => {
    if (!props.properties || showAdvancedFields) {
      return 0;
    }

    return props.properties.filter((property: any) => isAdvancedField(property)).length;
  }, [props.properties, showAdvancedFields]);

  const actionRail = props.formContext?.__actionRail || {};
  const canShowModifyActions = Boolean(actionRail.isModify && actionRail.requestId);
  const showCancelAction = props.uiSchema?.prStatus === 'active';
  const deleteDisabled = props.uiSchema?.statusType === 'delete' || props.uiSchema?.status === 'cancelled' || Boolean(props.uiSchema?.fileNotTracked);
  const railButtonSx = {
    width: { xs: 'auto', xl: '100%' },
    minWidth: { xs: 150, xl: 0 },
    alignSelf: { xs: 'flex-start', xl: 'stretch' },
    textTransform: 'none'
  };
  const deleteDisabledReason =
    props.uiSchema?.statusType === 'delete' || props.uiSchema?.status === 'cancelled'
      ? 'Delete not allowed in this status'
      : props.uiSchema?.fileNotTracked
        ? 'File not tracked - delete not allowed'
        : '';
  const railErrors = Array.isArray(actionRail.formErrors) ? actionRail.formErrors : [];
  const isNestedObjectTemplate = props?.idSchema?.$id && props.idSchema.$id !== 'root';
  const fallbackColumn = isNestedObjectTemplate ? "6" : "12";

  const fieldOrderMap = useMemo(() => {
    const orderedFieldNames: string[] = [];
    const seen = new Set<string>();
    const addField = (fieldName: string) => {
      if (!fieldName || seen.has(fieldName)) {
        return;
      }
      seen.add(fieldName);
      orderedFieldNames.push(fieldName);
    };

    groups.forEach((group: any) => {
      (Array.isArray(group?.fields) ? group.fields : []).forEach((fieldName: string) => addField(fieldName));
    });

    Object.keys(props.schema?.properties || {}).forEach((fieldName: string) => addField(fieldName));

    return new Map(orderedFieldNames.map((fieldName, index) => [fieldName, index]));
  }, [groups, props.schema]);

  const sortedRailErrors = useMemo(() => {
    type RailErrorEntry = {
      error: any;
      index: number;
      rootField: string | null;
    };

    return railErrors
      .map((error: any, index: number): RailErrorEntry => ({
        error,
        index,
        rootField: getRootErrorField(error?.property)
      }))
      .sort((a: RailErrorEntry, b: RailErrorEntry) => {
        const aOrder = a.rootField ? fieldOrderMap.get(a.rootField) : undefined;
        const bOrder = b.rootField ? fieldOrderMap.get(b.rootField) : undefined;

        const aHasOrder = typeof aOrder === 'number';
        const bHasOrder = typeof bOrder === 'number';

        if (aHasOrder && bHasOrder && aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        if (aHasOrder !== bHasOrder) {
          return aHasOrder ? -1 : 1;
        }

        return a.index - b.index;
      })
        .map((entry: RailErrorEntry) => entry.error);
  }, [fieldOrderMap, railErrors]);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  return (
    <>
      {versionFallback && (
        <Alert
          severity="warning"
          sx={{
            mt: 'calc(var(--surface-pad-y, 12px) * -1)',
            ml: 'calc(var(--surface-pad-x, 12px) * -1)',
            mr: 'calc(var(--surface-pad-x, 12px) * -1)',
            mb: 1.5,
            borderTopLeftRadius: `13px`,
            borderTopRightRadius: `13px`,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          }}
        >
          Configured version "{versionFallback.requestedVersion}" was not found. Module was loaded from latest available version "{versionFallback.fallbackVersion}".
        </Alert>
      )}

      <Box className="surface-header-strip" sx={{ mb: 2.4 }}>
        <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignItems: 'center' }}>
          <Typography component="h1" variant="inherit" className="page-title" sx={{ m: 0 }}>
            {props.title}
          </Typography>
          <Chip label={props.uiSchema["ui:moduleVersion"]} />
          <RequestStatusChip
            status={props.uiSchema.status}
            type={props.uiSchema.statusType}
            description={props.uiSchema.statusDescription}
            trailingIcon={props.uiSchema.fileFromMainFallback ? (
              <span style={{ fontSize: '0.7rem', color: '#fff', cursor: 'default' }}>*</span>
            ) : undefined}
          />
          {props.uiSchema["ui:hasAccessChecks"] && (
            <Tooltip title="Access control enabled - ownership verification required" placement="right" arrow>
              <Chip
                icon={<LockIcon />}
                color="warning"
                variant="outlined"
                label="Access Control"
              />
            </Tooltip>
          )}
          {!props.uiSchema["ui:approvalRequired"] && (
            <Tooltip title="This request will be automatically approved" placement="right" arrow>
              <Chip
                icon={<AutoModeIcon />}
                color="default"
                label="Auto approve"
              />
            </Tooltip>
          )}
          {hasAdvancedFields && (
            <Button
              size="small"
              variant={showAdvancedFields ? "contained" : "outlined"}
              onClick={() => setShowAdvancedFields((prev) => !prev)}
              sx={{ ml: 'auto' }}
            >
              {showAdvancedFields ? 'Hide advanced fields' : `Show advanced fields${hiddenAdvancedCount > 0 ? ` (${hiddenAdvancedCount})` : ''}`}
            </Button>
          )}
        </Stack>
      </Box>

      {descriptionLines.length > 0 && (
        <Box sx={{ mt: 1.6, mb: 3.6, p: 2 }}>
          {(showDescriptionDetails ? descriptionLines : descriptionLines.slice(0, 2)).map((item: string, idx: number) => (
            <Box key={idx} sx={{ mb: 0.85 }}>
              <MarkdownText>{item}</MarkdownText>
            </Box>
          ))}
          {descriptionLines.length > 2 && (
            <Button
              variant="text"
              size="small"
              onClick={() => setShowDescriptionDetails((prev) => !prev)}
              sx={{ mt: 0.5, px: 0 }}
            >
              {showDescriptionDetails ? 'Show less' : 'Show more'}
            </Button>
          )}
        </Box>
      )}

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pt: descriptionLines.length > 0 ? 2.5 : 0.4,
          display: { xs: 'block', xl: 'grid' },
          gridTemplateColumns: { xl: 'minmax(0, 1fr) 320px' },
          columnGap: { xl: 2.5 },
          alignItems: 'start'
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {groups.map((group: any, index: number) => {
            const childProps = getPropsForGroup(group, props);
            const visibleProperties = getVisibleProperties(childProps.properties, showAdvancedFields);
            const existingFieldNames = new Set(childProps.properties.map((item: any) => item.name));
            const deferredMissingProperties = (Array.isArray(group?.fields) ? group.fields : [])
              .filter((fieldName: string) => !existingFieldNames.has(fieldName))
              .filter((fieldName: string) => shouldShowDeferredPlaceholderForField(props.schema, props.uiSchema, fieldName, showAdvancedFields))
              .map((fieldName: string) => {
                const fieldSchema = props.schema?.properties?.[fieldName] || {};
                const uiSchemaForField = props.uiSchema?.[fieldName] || {};
                const column = String(uiSchemaForField?.['ui:column'] ?? fallbackColumn);

                return {
                  name: fieldName,
                  __deferredPlaceholder: true,
                  content: { props: { schema: fieldSchema, uiSchema: uiSchemaForField } },
                  hidden: false,
                  title: fieldSchema?.title || fieldName,
                  column
                };
              });
            const propertiesToRender = visibleProperties.concat(deferredMissingProperties);

            if (propertiesToRender.length === 0) {
              return null;
            }

            const visibleFieldNames = visibleProperties
              .filter((item: any) => !isDeferredSelectLikeField(item))
              .map((item: any) => item.name);
            const requiredFields = visibleFieldNames.filter((field: string) => props.schema?.required?.includes(field));
            const completedRequired = requiredFields.filter((field: string) => isValueProvided(props.formData?.[field])).length;
            const isComplete = requiredFields.length > 0 && completedRequired === requiredFields.length;

            if (group.title !== '') {
              return (
                <Accordion
                  key={"panel" + index}
                  defaultExpanded={index === 0}
                  expanded={expanded === 'panel' + index}
                  onChange={handleChange('panel' + index)}
                  sx={{
                    border: `1px solid ${uiTokens.borderColor}`,
                    borderRadius: `${uiTokens.radius.md * 4}px !important`,
                    mt: 0,
                    mb: 2,
                    backgroundColor: uiTokens.surface,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      mt: 0,
                      mb: 2
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    id={"panel" + index + "-header"}
                    key={"accordionsummary-" + index}
                    sx={{
                      backgroundColor: 'rgb(247, 247, 247)',
                      minHeight: 56,
                      py: 0.45,
                      borderBottom: '1px solid transparent',
                      '& .MuiAccordionSummary-content': {
                        mt: 1.1,
                        mb: 1.7
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        mt: 1.1,
                        mb: 1.7
                      },
                      '&.Mui-expanded': {
                        borderBottomColor: uiTokens.borderColor
                      }
                    }}
                  >
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                      <strong>{group.title}</strong>
                      {requiredFields.length > 0 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={isComplete ? 'success' : 'default'}
                          label={`${completedRequired}/${requiredFields.length} required`}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails key={"accordiondetails-" + index} sx={{ pt: 5, pb: 2.2 }}>
                    <Grid key={"grid-accordiondetails-" + index} container spacing={2} sx={{ minWidth: 0 }}>
                      {renderField(propertiesToRender, fallbackColumn)}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            }

            return (
              <Grid key={"grid-group-" + index} container spacing={2} sx={{ minWidth: 0 }}>
                {renderField(propertiesToRender, fallbackColumn)}
              </Grid>
            );
          })}
        </Box>

        <Box sx={{ mt: 0, pt: { xs: 3, xl: 0 }, position: { xl: 'sticky' }, top: { xl: 92 }, alignSelf: 'start' }}>
          {sortedRailErrors.length > 0 && (
            <Alert
              severity="error"
              icon={false}
              sx={{
                mb: 1.5,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.8 }}>
                <ErrorOutlineIcon fontSize="small" />
                <span>Validation summary</span>
              </AlertTitle>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  listStyleType: 'none',
                  '& li': {
                    ml: 0,
                    p: 0,
                    width: '100%'
                  }
                }}
              >
                {sortedRailErrors.map((error: any, idx: number) => (
                  <li key={`rail-error-${idx}`}>
                    <Button
                      variant="text"
                      size="small"
                      sx={{
                        px: '10px',
                        py: 0.35,
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        color: '#1f1f1f',
                        fontWeight: 500,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        width: '100%',
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                      onClick={() => actionRail.onSelectError?.(error?.property)}
                    >
                      {getFriendlyValidationMessage(error)}
                    </Button>
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          <Box sx={{ border: `1px solid ${uiTokens.borderColor}`, borderRadius: uiTokens.radius.md, backgroundColor: uiTokens.surfaceMuted, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.2 }}>Actions</Typography>
            <Stack
              spacing={1}
              useFlexGap
              direction={{ xs: 'row', xl: 'column' }}
              sx={{
                alignItems: { xs: 'flex-start', xl: 'stretch' },
                flexWrap: { xs: 'wrap', xl: 'nowrap' }
              }}
            >
              <Button
                type="button"
                variant="contained"
                size="medium"
                onClick={() => actionRail.onSubmitDefault?.()}
                disabled={Boolean(actionRail.disableSubmit)}
                sx={{ ...railButtonSx, backgroundColor: uiTokens.accent }}
              >
                {actionRail.isModify ? 'Update' : 'Create'}
              </Button>
              {actionRail.canUseDryRun && (
                <Button
                  type="button"
                  variant="contained"
                  size="medium"
                  onClick={() => actionRail.onTriggerDryRun?.()}
                  disabled={Boolean(actionRail.disableSubmit)}
                  sx={{
                    ...railButtonSx,
                    backgroundColor: '#f0c419',
                    color: '#000',
                    '&:hover': {
                      backgroundColor: '#d7ae16'
                    }
                  }}
                >
                  {actionRail.isModify ? 'Update (dry run)' : 'Create (dry run)'}
                </Button>
              )}
              {canShowModifyActions && (
                showCancelAction ? (
                  <Button
                    type="button"
                    variant="outlined"
                    size="medium"
                    color="warning"
                    startIcon={<CancelIcon />}
                    onClick={() => actionRail.onOpenCancel?.()}
                    sx={railButtonSx}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Tooltip title={deleteDisabledReason}>
                    <Box component="span" sx={{ display: 'block', width: { xs: 'auto', xl: '100%' }, minWidth: { xs: 150, xl: 0 } }}>
                      <Button
                        type="button"
                        variant="outlined"
                        size="medium"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => actionRail.onOpenDelete?.()}
                        disabled={deleteDisabled}
                        sx={railButtonSx}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Tooltip>
                )
              )}
            </Stack>

            {actionRail.isLoading && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.4, alignItems: 'center' }} role="status" aria-live="polite">
                <CircularProgress size={16} />
                <span style={{ fontSize: '0.85rem' }}>{actionRail.loadingMessage || 'Submitting request...'}</span>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}


// create a temporary theme to get the default options
const defaultTheme = createTheme();

// get the default `shadows` object
const defaultShadows: ThemeOptions['shadows'] = [...defaultTheme.shadows];

const theme = createTheme({
  shadows: defaultShadows.map(() => 'none') as Shadows,
  components: {
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: "#db3131",
          "&$error": {
            color: "#db3131",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            fontSize: "0.9rem"
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.86rem"
          },
          "& .MuiFormHelperText-root": {
            fontSize: "0.76rem"
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(0.75)"
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          "& .MuiTypography-root": {
            fontSize: "0.9rem"
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&": {
            marginTop: "0px"
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2
          }
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#7f8ea3',
          width: '95%',
          marginLeft: 'auto',
          marginRight: 'auto',
          display: 'block'
        },
        rail: {
          backgroundColor: '#d4dce6',
          opacity: 1
        },
        track: {
          border: 'none',
          backgroundColor: '#8e9eb4'
        },
        thumb: {
          width: 22,
          height: 22,
          backgroundColor: '#f7f9fc',
          border: '1px solid #8a98ad',
          '&:hover, &.Mui-focusVisible': {
            boxShadow: '0 0 0 5px rgba(127, 142, 163, 0.22)'
          },
          '&.Mui-active': {
            boxShadow: '0 0 0 8px rgba(127, 142, 163, 0.24)'
          }
        }
      }
    }
  },
});

function disableImmutableFields(subUiSchema: any, status: string, statusType: any, key: string = "", uiSchema: any = {}): void {
  if (subUiSchema['immutable'] && !(status === "pending" && statusType === "create")) {
    uiSchema[key]["ui:disabled"] = true;
    uiSchema[key]["ui:description"] = "This field cannot be modified";
  }

  for (const key in subUiSchema) {
    if (typeof subUiSchema[key] === 'object' && subUiSchema[key] !== null) {
      disableImmutableFields(subUiSchema[key], status, statusType, key, subUiSchema);
    }
  }
}

function prepareUiSchemaToDisableFieldsInModify(uiSchema: any, status: string, statusType: string) {
  ["region", "subscription", "location", "resource_group_name", "iac_custom_project"].forEach(field => {
    if (uiSchema[field]) {
      uiSchema[field]["ui:disabled"] = true;
      uiSchema[field]["ui:description"] = "This field cannot be modified";
    }
  });

  disableImmutableFields(uiSchema, status, statusType);
}

function resolveReadOnlyValue(value: any, formContext: any): boolean {
  const resolvedValue = typeof value === 'string' && value.includes('{{')
    ? renderWithEval(value, formContext)
    : value;

  if (typeof resolvedValue === 'boolean') {
    return resolvedValue;
  }

  if (typeof resolvedValue === 'string') {
    return resolvedValue.toLowerCase() === 'true';
  }

  return false;
}

function applyReadOnlyUiSchema(uiSchema: any, formContext: any): any {
  if (!uiSchema || typeof uiSchema !== 'object' || Array.isArray(uiSchema)) {
    return uiSchema;
  }

  const resolvedUiSchema = { ...uiSchema };
  const readOnly = resolvedUiSchema["ui:options"]?.readOnly;

  if (readOnly !== undefined && resolveReadOnlyValue(readOnly, formContext)) {
    resolvedUiSchema["ui:disabled"] = true;
    resolvedUiSchema["ui:description"] = hiddenDescriptionSpacing;
  }

  Object.keys(resolvedUiSchema).forEach(key => {
    if (!key.startsWith("ui:") && resolvedUiSchema[key] && typeof resolvedUiSchema[key] === 'object' && !Array.isArray(resolvedUiSchema[key])) {
      resolvedUiSchema[key] = applyReadOnlyUiSchema(resolvedUiSchema[key], formContext);
    }
  });

  return resolvedUiSchema;
}

function ModulePageSkeleton({ id }: { id?: string }) {
  return (
    <Box aria-hidden="true" sx={{ width: '100%', display: 'flex', justifyContent: 'center', pb: 2, px: 0, boxSizing: 'border-box' }}>
      <Box
        sx={{
          '--surface-pad-x': { xs: '12px', sm: '20px', md: '24px' },
          '--surface-pad-y': { xs: '12px', sm: '20px', md: '24px' },
          width: '100%',
          minWidth: 0,
          backgroundColor: uiTokens.surface,
          border: `1px solid ${uiTokens.borderColor}`,
          borderRadius: uiTokens.radius.lg,
          boxShadow: uiTokens.shadow,
          p: 'var(--surface-pad-y) var(--surface-pad-x)',
          boxSizing: 'border-box'
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2.4 }}>
          <Skeleton variant="text" width="clamp(260px, 42vw, 520px)" height={52} />
          <Skeleton variant="rounded" width={160} height={30} />
          <Skeleton variant="rounded" width={76} height={30} />
        </Stack>

        <Box sx={{ mt: 1.6, mb: 3.6, p: 2 }}>
          <Skeleton variant="text" width="100%" height={50} />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            pt: 2.5,
            display: { xs: 'block', xl: 'grid' },
            gridTemplateColumns: { xl: 'minmax(0, 1fr) 320px' },
            columnGap: { xl: 2.5 },
            alignItems: 'start'
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack spacing={2.8} useFlexGap sx={{ minWidth: 0 }}>
            <Box sx={{ border: `1px solid ${uiTokens.borderColor}`, borderRadius: `${uiTokens.radius.md * 4}px`, backgroundColor: uiTokens.surface, p: 2 }}>

              <Grid container spacing={2} sx={{ minWidth: 0 }}>
                <Grid item xs={12} md={4}>
                  <Skeleton variant="rounded" height={52} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Skeleton variant="rounded" height={52} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Skeleton variant="rounded" height={52} />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton variant="rounded" height={52} />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 1.8, borderRadius: `${uiTokens.radius.md * 4}px`, border: `1px solid ${uiTokens.borderColor}` }}>
              <Skeleton variant="text" width={220} height={32} />
            </Box>
            <Box sx={{ p: 1.8, borderRadius: `${uiTokens.radius.md * 4}px`, border: `1px solid ${uiTokens.borderColor}` }}>
              <Skeleton variant="text" width={260} height={32} />
            </Box>
            </Stack>
          </Box>

          <Box sx={{ mt: 0, pt: { xs: 3, xl: 0 }, position: { xl: 'sticky' }, top: { xl: 92 }, alignSelf: 'start' }}>
            <Box sx={{ border: `1px solid ${uiTokens.borderColor}`, borderRadius: uiTokens.radius.md, backgroundColor: uiTokens.surfaceMuted, p: 1.5 }}>
              <Skeleton variant="text" width={90} height={28} sx={{ mb: 1.2 }} />
              <Skeleton variant="rounded" width="100%" height={38} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" width="100%" height={38} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

async function fixHtmlEncodings(input: string[]) {
  return await input.map((e: string) => e.replace("%3C", "<").replace("%3E", ">"));
}

async function fixLegacyInputs(schema: any, data: any): Promise<any> {
  const prepSchema = { ...schema };
  delete prepSchema.name;

  const resolveLegacyInput = async (error: ErrorObject) => {
    const betterErrors = betterAjvErrors({ schema: prepSchema, data: data, errors: [error], basePath: "" });
    const segments = betterErrors?.[0].path.split('.').slice(1) || [];
    let current = data;

    for (const attrSegment of await fixHtmlEncodings(segments)) {
      current = current[attrSegment];
    }

    const schemaSegments = error.schemaPath.split('/').slice(1) || [];
    return {schemaSegments: await fixHtmlEncodings(schemaSegments), inputValue: current};
  };

  const ajv = new Ajv({ allErrors: true });
  ajv.addFormat("data-url", {
    type: "string",
    validate: (data: any) => {
      const regex = /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+)?(;[a-zA-Z-]+=[a-zA-Z0-9-]+)*(;base64)?,(.*)$/;
      return regex.test(data);
    }
  });

  const validate = ajv.compile(prepSchema);
  const valid = validate(data);

  if (!valid && validate.errors) {
    for (const error of validate.errors) {
      // skip all validation errors != anyOf
      if(error.keyword !== "anyOf") {
        continue;
      }

      const {schemaSegments, inputValue} = await resolveLegacyInput(error);

      let current = prepSchema;
      for (const segment of schemaSegments) {
        if (current[segment] !== undefined) {
          if (segment === "anyOf") {
            const duplicatedLastEntry = JSON.parse(JSON.stringify(current[segment][current[segment].length - 1]))
            duplicatedLastEntry.enum = [`${inputValue}`];
            duplicatedLastEntry.title = `(LEGACY) ${inputValue}`
            current[segment].push(duplicatedLastEntry);
            console.log(`Fix legacy enum error: ${error.keyword} (${duplicatedLastEntry.title})`)
          }
          current = current[segment];
        } else {
          throw new Error(`Path segment ${segment} not found in schema`);
        }
      }
    }
  }
  return schema;
}

function RegistryModule({ platform, moduleName, id }: { platform: string, moduleName: string, id?: string }) {
  const [moduleData, setModuleData] = useState<ApiModuleData>();
  const [formData, setFormData] = useState<any>();
  const [initialFormData, setInitialFormData] = useState<any>(); // Store initial data for comparison
  const [showRequestResult, setShowRequestResult] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string>("");
  const [requestResultTitle, setRequestResultTitle] = useState<string>("Success");
  const [dryRunHclOutput, setDryRunHclOutput] = useState<string>("");
  const [dryRunTargetPaths, setDryRunTargetPaths] = useState<string[]>([]);
  const [isDryRunClicked, setWasDryRunClicked] = useState<boolean>(false);
  const [submitMode, setSubmitMode] = useState<'default' | 'dry-run'>('default');
  const submitModeRef = useRef<'default' | 'dry-run'>('default');
  const [currentModuleId, setCurrentModuleId] = useState<string>(""); // Only relevant for update
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<any[]>([]);
  const [expandedGroupIndex, setExpandedGroupIndex] = useState<number | null>(null);
  const [resultRequestId, setResultRequestId] = useState<string | undefined>(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [backendApiService, setBackendApiService] = useState<BackendApiService>();
  const [initialFormDataSet, setInitialFormDataSet] = useState<boolean>(false); // Track if initial form data is captured
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [currentResourceName, setCurrentResourceName] = useState<string>(moduleName);
  const initialCaptureTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialFormDataSetRef = useRef<boolean>(false);
  const [accessToken] = useState(() => {
    return getAccessToken();
  });
  const canUseDryRun = useMemo(() => {
    if (!accessToken) {
      return false;
    }

    try {
      return isAdmin(accessToken);
    } catch {
      return false;
    }
  }, [accessToken]);

  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<any>(null);

  const setSubmitModeSafe = (mode: 'default' | 'dry-run') => {
    submitModeRef.current = mode;
    setSubmitMode(mode);
  };

  // Check if form has changes - calculated on every render
  const hasChanges = useMemo(() => {
    if (!id || !initialFormData || !formData || !moduleData?.schema) {
      return false;
    }

    const normalizedCurrent = normalizeFormDataForSchema(formData, moduleData.schema);
    const normalizedInitial = normalizeFormDataForSchema(initialFormData, moduleData.schema);

    return JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial);
  }, [id, initialFormData, formData, moduleData?.schema]);

  // Apply overrideDefault values to formData
  const applyOverrides = (data: any, schema: any, uiSchema: any) => {
    const overrideData = { ...data };
    
    Object.keys(schema.properties || {}).forEach(fieldName => {
      const fieldUiSchema = uiSchema[fieldName];
      const overrideDefault = fieldUiSchema?.["ui:options"]?.overrideDefault;
      
      if (overrideDefault) {
        try {
          const evaluatedValue = renderWithEval(overrideDefault, { ...overrideData, globalSchema: schema });
          console.log(`Apply override for ${fieldName}:`, evaluatedValue);
          const fieldSchemaType = schema?.properties?.[fieldName]?.type;
          
          // Handle boolean strings
          if (String(evaluatedValue).toLowerCase() === 'true') {
            overrideData[fieldName] = true;
          } else if (String(evaluatedValue).toLowerCase() === 'false') {
            overrideData[fieldName] = false;
          } else if ((fieldSchemaType === 'number' || fieldSchemaType === 'integer') && evaluatedValue !== null && evaluatedValue !== undefined && String(evaluatedValue).trim() !== '') {
            const numericValue = Number(evaluatedValue);
            if (!Number.isNaN(numericValue)) {
              overrideData[fieldName] = fieldSchemaType === 'integer' ? Math.trunc(numericValue) : numericValue;
            } else {
              overrideData[fieldName] = evaluatedValue;
            }
          } else {
            overrideData[fieldName] = evaluatedValue;
          }
        } catch (error) {
          console.error(`Error applying override for ${fieldName}:`, error);
        }
      }
    });
    
    return overrideData;
  };

  // TODO: Basic functionality for displaying PR comments
  // const [fieldComments, setFieldComments] = useState<{
  //   content: string,
  //   comments: {content: string, user: { displayName: string, uniqueName: string }}[]
  // }[]>([]);

  useEffect(() => {
    const prepareTFModule = async () => {
      try {
        const backendApiServiceObj = new BackendApiService(accessToken ?? '');
        setBackendApiService(backendApiServiceObj);

        const moduleData = await backendApiServiceObj.prepareModuleRequest(
          {
            name: moduleName,
            platform: platform
          },
          {
            requestId: id
          }
        );

        moduleData.uiSchema["ui:ObjectFieldTemplate"] = ObjectFieldTemplate;
        moduleData.uiSchema["ui:moduleVersion"] = moduleData.moduleVersion;
        moduleData.uiSchema["ui:versionFallback"] = moduleData.versionFallback;
        moduleData.uiSchema["ui:approvalRequired"] = moduleData.moduleMetaData.approvalRequired;
        moduleData.uiSchema["ui:hasAccessChecks"] = moduleData.moduleMetaData.hasAccessChecks;

        applyCustomArrayTemplatesRecursively(moduleData.schema, moduleData.uiSchema);

        if (id) {
          const existingData = await backendApiServiceObj.getUserRequest(id);

          if (!existingData.isEditAllowed) {
            throw new Error(existingData.statusDescription || 'No access to this pull request (currently in progress and locked by another user)');
          }

          const firstModule = existingData.modules?.[0];
          if (firstModule.platform !== platform || firstModule.name !== moduleName) {
            setErrorMessages(["Request for given platform and name not found"]);
          }

          // fix legacy input values -> if values are removed from meta data add those dynamically for modify requests
          moduleData.schema = await fixLegacyInputs(moduleData.schema, firstModule?.data);

          moduleData.schema.title = `Edit #${id} - ${moduleData.schema.title}`;
          // TODO: Support multiple modules in the future
          // TODO: Check isEditAllowed field

          // Reset baseline so stale state from previous requests cannot enable Update prematurely.
          setInitialFormData(undefined);
          setFormData(firstModule?.data);
          setInitialFormDataSet(false); // Will be set after form transforms the data
          initialFormDataSetRef.current = false;
          setCurrentModuleId(firstModule?.id);
          setCurrentResourceName(firstModule?.title || firstModule?.name || moduleName);
          // setFieldComments(existingData.comments);
          // moduleData.uiSchema.comments = existingData.comments;
          moduleData.uiSchema.status = existingData.status;
          moduleData.uiSchema.prStatus = existingData.prStatus;
          moduleData.uiSchema.statusType = existingData.statusType;
          moduleData.uiSchema.statusDescription = existingData.statusDescription;
          moduleData.uiSchema.fileNotTracked = existingData.modules?.[0]?.fileNotTracked;
          moduleData.uiSchema.fileFromMainFallback = existingData.modules?.[0]?.fileFromMainFallback;



          prepareUiSchemaToDisableFieldsInModify(moduleData.uiSchema, existingData.status, moduleData.uiSchema.statusType);
        } else {
          moduleData.uiSchema.status = "new";
          moduleData.uiSchema.statusType = "create";
          moduleData.uiSchema.statusDescription = "You are creating a new request";
          moduleData.schema.title = `Create ${moduleData.schema.title}`;

          const queryFormData = buildFormDataFromQueryParams(moduleData.schema, location.search);
          
          // Apply overrides to initial formData for new requests
          const initialData = applyOverrides(queryFormData, moduleData.schema, moduleData.uiSchema);
          setFormData({ ...initialData, ...queryFormData });
        }

        // Clean schema from undefined properties to prevent RJSF errors
        console.log('[Schema Cleanup] Cleaning schema...');
        const cleanedSchema = removeUndefinedFromSchema(moduleData.schema, 'schema');
        const cleanedUiSchema = removeUndefinedFromSchema(moduleData.uiSchema, 'uiSchema');
        
        if (cleanedSchema && cleanedUiSchema) {
          moduleData.schema = cleanedSchema;
          moduleData.uiSchema = cleanedUiSchema;
        } else {
          console.error('[Schema Cleanup] Failed to clean schema!', { cleanedSchema, cleanedUiSchema });
          throw new Error('Invalid schema structure detected');
        }

        setModuleData(moduleData);
      } catch (error) {
        if (error instanceof MultiError) {
          setErrorMessages(error.errors);
        } else if (error instanceof Error) {
          setErrorMessages([error.message]);
        } else {
          setErrorMessages([(error as any).toString()]);
        }
        console.log(error);
      }
    };

    prepareTFModule();
  }, [accessToken, platform, moduleName, id, location.search]);

  const extractDryRunHcl = (result: any): string => {
    return result?.metaData?.hclModuleContent || '';
  };

  const extractDryRunTargetPaths = (result: any): string[] => {
    const toDisplayPath = (repository: any, filePath: any): string => {
      const repo = typeof repository === 'string' ? repository : '';
      const path = typeof filePath === 'string' ? filePath : '';

      if (repo && path) {
        return `${repo}:${path}`;
      }

      return path || repo || '';
    };

    const modules = result?.metaData?.modules;
    if (Array.isArray(modules)) {
      return modules
        .map((module) => toDisplayPath(module?.moduleRepository, module?.moduleFilePath))
        .filter((path): path is string => typeof path === 'string' && path.length > 0);
    }

    const singlePath = toDisplayPath(result?.metaData?.moduleRepository, result?.metaData?.moduleFilePath);
    if (typeof singlePath === 'string' && singlePath.length > 0) {
      return [singlePath];
    }

    return [];
  };

  const onSubmit = (async (data: any, error: any) => {
    const formData = data.formData;
    const isDryRun = submitModeRef.current === 'dry-run';

    if (!moduleData) {
      console.log("Error: no hclData object set!");
      return;
    }

    if (!backendApiService) {
      console.log("Error: no adoApiService object set!");
      return;
    }

    // Avoid aria-hidden focus warnings when MUI Backdrop/Dialog opens.
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }

    setIsLoading(true);
    setLoadingMessage(isDryRun ? 'Running dry run...' : (id ? 'Updating request...' : 'Creating request...'));
    setRequestError(""); // Reset error before new submit
    setDryRunHclOutput("");
    setDryRunTargetPaths([]);
    setResultRequestId(id);
    setWasDryRunClicked(isDryRun);

    try {
      let response;
      if (id) {
        response = await backendApiService.updateModuleRequest(
          platform, moduleName, id,
          { moduleId: currentModuleId, formData: formData },
          { dryRun: isDryRun }
        );
      } else {
        response = await backendApiService.createModuleRequest(
          {
            name: moduleName,
            platform: platform,
            formData: formData
          },
          { dryRun: isDryRun }
        );
      }

      if (isDryRun) {
        setRequestResultTitle(id ? 'Dry-Update Result' : 'Dry-Create Result');
        setDryRunHclOutput(extractDryRunHcl(response));
        setDryRunTargetPaths(extractDryRunTargetPaths(response));
      } else {
        setRequestResultTitle('Success');
        setResultRequestId(id || extractRequestIdFromStatus(response));
      }
    } catch (err) {
      console.log(err);
      setRequestResultTitle('Error');
      setResultRequestId(undefined);
      if (err instanceof MultiError) {
        setRequestError(err.errors.join('\n'));
      } else if (err instanceof Error) {
        setRequestError(err.message);
      } else {
        setRequestError(String(err));
      }
    }
    setSubmitModeSafe('default');
    setShowRequestResult(true);
    setIsLoading(false);
    setLoadingMessage('');
  });

  const triggerDryRun = () => {
    setSubmitModeSafe('dry-run');
    formRef.current?.submit();
  };

  const triggerDefaultSubmit = () => {
    setSubmitModeSafe('default');
    formRef.current?.submit();
  };

  const handleValidationSelect = (property: string | undefined) => {
    const rootErrorField = getRootErrorField(property);
    const targetGroupIndex = findGroupIndexForField(moduleData?.uiSchema?.groups || [], rootErrorField);

    if (targetGroupIndex !== null) {
      setExpandedGroupIndex(targetGroupIndex);
    }

    requestAnimationFrame(() => {
      focusFormField(rootErrorField || property || undefined);
    });
  };

  const disableSubmitActions = isLoading || (id ? (!initialFormDataSet || !hasChanges) : false);

  const widgets: RegistryWidgetsType = {
    autocomplete: FormAutocomplete,
    customdate: CustomDate,
    SelectWidget: CustomSelectWidget,
    text: CustomInputWidget,
  };

  function transformErrors(errors: any, uiSchema: any) {
    return errors
      .filter((error: any) => error.name !== 'enum') // remove enum errors from error list
      .filter((error: any) => {
        // Filter out validation errors for disabled fields (including immutable fields)
        let errorProperty = error.property.replace(/^\.+|\.+$/g, ''); // can contain dots
        if (!errorProperty) return true; // Keep errors without property (root level)
        
        const errorPropertyElements = errorProperty.split(".");
        let errorPropertyUiSchemaPath = uiSchema;
        
        if (errorPropertyElements.length > 1) {
          errorPropertyElements.forEach((e: string) => {
            if (!isNaN(Number(e))) {
              e = "items";
            }
            if (errorPropertyUiSchemaPath[e]) {
              errorPropertyUiSchemaPath = errorPropertyUiSchemaPath[e];
            }
          })
        } else if (errorPropertyElements.length === 1) {
          errorPropertyUiSchemaPath = errorPropertyUiSchemaPath[errorPropertyElements[0]];
        }
        
        // Skip validation errors for disabled fields (immutable fields will be disabled during modify)
        if (errorPropertyUiSchemaPath?.["ui:disabled"]) {
          return false;
        }
        
        return true;
      })
      .map((error: any) => {
        //console.log("error", error, uiSchema);
        let errorProperty = error.property.replace(/^\.+|\.+$/g, ''); // can contain dots
        const errorPropertyElements = errorProperty.split(".");

        let errorPropertyUiSchemaPath = uiSchema;
        if (errorPropertyElements.length > 1) {
          errorPropertyElements.forEach((e: string) => {
            if (!isNaN(Number(e))) {
              e = "items";
            }
            if (errorPropertyUiSchemaPath[e]) {
              errorPropertyUiSchemaPath = errorPropertyUiSchemaPath[e];
            }
          })
        } else if (errorPropertyElements.length === 1) {
          errorPropertyUiSchemaPath = errorPropertyUiSchemaPath[errorPropertyElements[0]];
        }
        const uiSchemaError = errorPropertyUiSchemaPath?.["ui:options"]?.error;
        if (error.name === 'pattern' && uiSchemaError) {
          error.message = uiSchemaError;
          error.stack = uiSchemaError;
        } else if (error.name === 'anyOf') {
          error.message = error.stack.replace("must match a schema in anyOf", "must be selected");
        }
        return error;
      });
  }

  // TODO: Custom validate to show comments from pull requests
  // function customValidate(formData: any, errors: any, uiSchema: any) {
  //   if (id) {
  //     fieldComments.forEach(c => {
  //       errors[c.content].addError(c.comments.map(sc => `Comment from ${sc.user.displayName}: ${sc.content}`).join(", "));
  //     })
  //   }
  //   return errors;
  // }

  const handleClose = () => {
    setShowRequestResult(false);
    setDryRunHclOutput('');
    setDryRunTargetPaths([]);
    if (process.env.REACT_APP_ENVIRONMENT === 'production') {
      navigate('/user/requests');
    }
  };

  const handleNavigateAfterResult = (target: string) => {
    setShowRequestResult(false);
    setDryRunHclOutput('');
    setDryRunTargetPaths([]);
    navigate(target);
  };

  const parseErrorMessage = (errorMessage: string) => {
    // Try to extract JSON error structure from error message
    const jsonMatch = errorMessage.match(/\{"errors":\[.*?\]\}/);
    if (jsonMatch) {
      try {
        const errorData = JSON.parse(jsonMatch[0]);
        if (errorData.errors && Array.isArray(errorData.errors)) {
          return errorData.errors.map((err: any) => ({
            status: err.status,
            title: err.title
          }));
        }
      } catch (e) {
        // If JSON parsing fails, return original message
      }
    }
    return null;
  };

  const extractFirstUrl = (message: string): string | undefined => {
    const match = message.match(/https?:\/\/[^\s]+/);
    return match ? match[0] : undefined;
  };

  const renderFriendlyError = (message: string) => {
    const url = extractFirstUrl(message);
    const cleanedMessage = (url ? message.replace(url, "").trim() : message).replace(/\n{3,}/g, "\n\n");

    return (
      <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
        <div style={{ whiteSpace: "pre-wrap" }}>{cleanedMessage}</div>
        {url && (
          <Button
            sx={{ mt: 2 }}
            size="small"
            variant="outlined"
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          >
            Open existing request
          </Button>
        )}
      </Alert>
    );
  };

  const dialogContent = () => {
    if (!requestError && dryRunHclOutput) {
      return (
        <span>
          Dry-Run completed successfully.
          <br /><br />
          {dryRunTargetPaths.length > 0 && (
            <>
              Resulting target path{dryRunTargetPaths.length > 1 ? 's' : ''}:
              <br />
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  margin: '6px 0 0 0'
                }}
              >
                {dryRunTargetPaths.join("\n")}
              </pre>
              <br />
            </>
          )}
          The following HCL would be committed:
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem', margin: 0 }}>{dryRunHclOutput}</pre>
        </span>
      );
    }

    if (!requestError) {
      return (<span>The request was successfully {id ? "updated" : "created"}.</span>);
    } else {
      const parsedErrors = parseErrorMessage(requestError);
      
      if (parsedErrors) {
        return (<span>
          The request could not be {id ? "updated" : "created"}.
          <br /><br />
          {parsedErrors.map((err: any, idx: number) => (
            <div key={idx} style={{ marginBottom: '10px' }}>
              <strong>Status:</strong> {err.status}<br />
              <strong>Error:</strong> {err.title}
            </div>
          ))}
        </span>);
      } else {
        return (<span>
          The request could not be {id ? "updated" : "created"}.
          <br /><br />
          {renderFriendlyError(requestError)}
        </span>);
      }
    }
  }

  const formContext = {
    ...formData,
    globalSchema: moduleData?.schema,
    __isModify: Boolean(id),
    __expandedGroup: expandedGroupIndex,
    __actionRail: {
      formErrors,
      canUseDryRun,
      isLoading,
      loadingMessage,
      disableSubmit: disableSubmitActions,
      isModify: Boolean(id),
      requestId: id,
      onSubmitDefault: triggerDefaultSubmit,
      onTriggerDryRun: triggerDryRun,
      onSelectError: handleValidationSelect,
      onOpenCancel: () => setCancelDialogOpen(true),
      onOpenDelete: () => setDeleteDialogOpen(true)
    }
  };

  const uiSchema = moduleData ? applyReadOnlyUiSchema({
    ...moduleData.uiSchema,
    "ui:submitButtonOptions": {
      norender: true
    }
  }, formContext) : undefined;

  return (
    errorMessages.length > 0 ? (
      <Alert severity="error" >
        <AlertTitle>Error loading module</AlertTitle>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {errorMessages.map((err: string, idx: number) => (
            <li key={idx}>{err}</li>
          ))}
        </ul>
      </Alert>
    ) : (
      <div>
        {
          moduleData ? (
            <ThemeProvider theme={theme}>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', pb: 2, px: 0, boxSizing: 'border-box' }}>
                <Box
                  sx={{
                    '--surface-pad-x': { xs: '12px', sm: '20px', md: '24px' },
                    '--surface-pad-y': { xs: '12px', sm: '20px', md: '24px' },
                    width: '100%',
                    minWidth: 0,
                    backgroundColor: uiTokens.surface,
                    border: `1px solid ${uiTokens.borderColor}`,
                    borderRadius: uiTokens.radius.lg,
                    boxShadow: uiTokens.shadow,
                    p: 'var(--surface-pad-y) var(--surface-pad-x)',
                    boxSizing: 'border-box'
                  }}
                >
                  <Form
                    ref={formRef}
                    schema={moduleData?.schema}
                    formData={formData}
                    formContext={formContext}
                    onChange={({ formData: newFormData, errors }) => {
                      // Debounced capture: wait for RJSF to finish all initial transforms before capturing baseline
                      if (id && !initialFormDataSetRef.current && newFormData) {
                        if (initialCaptureTimer.current) {
                          clearTimeout(initialCaptureTimer.current);
                        }
                        initialCaptureTimer.current = setTimeout(() => {
                          if (!initialFormDataSetRef.current) {
                            setInitialFormData(JSON.parse(JSON.stringify(newFormData)));
                            setInitialFormDataSet(true);
                            initialFormDataSetRef.current = true;
                          }
                        }, 100);
                      }

                      setFormErrors(errors || []);
                      if (!errors || errors.length === 0) {
                        setExpandedGroupIndex(null);
                      }

                      setFormData(newFormData);
                    }}
                    uiSchema={uiSchema}
                    validator={validator}
                    widgets={widgets}
                    templates={{
                      DescriptionFieldTemplate,
                      FieldTemplate,
                      ArrayFieldTemplate: CustomArrayFieldTemplate,
                      ArrayFieldItemTemplate: CustomArrayFieldItemTemplate
                    }}
                    transformErrors={transformErrors}
                    onSubmit={onSubmit}
                    onError={(errors) => {
                      setFormErrors(errors || []);

                      const firstErrorField = getRootErrorField(errors?.[0]?.property);
                      const targetGroupIndex = findGroupIndexForField(moduleData?.uiSchema?.groups || [], firstErrorField);
                      if (targetGroupIndex !== null) {
                        setExpandedGroupIndex(targetGroupIndex);
                      }

                      if (errors?.[0]?.property || firstErrorField) {
                        requestAnimationFrame(() => {
                          focusFormField(firstErrorField || errors?.[0]?.property || undefined);
                        });
                      }

                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    showErrorList={false}
                    noHtml5Validate={true}
                    liveOmit={true}
                    omitExtraData={true}
                  />
                </Box>
              </Box>
            </ThemeProvider>
          ) : (
            <ModulePageSkeleton id={id} />
          )
        }
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1
          }}
          open={!moduleData}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Dialog open={showRequestResult} TransitionProps={{ onExited: () => setRequestError("") }}>
          <DialogTitle>
            {requestResultTitle}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {dialogContent()}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {!requestError && !isDryRunClicked && resultRequestId && (
              <Button onClick={() => handleNavigateAfterResult(`/user/requests/${resultRequestId}`)} variant="outlined">
                View request details
              </Button>
            )}
            <Button onClick={handleClose} autoFocus>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {id && (
          <>
            <DeleteResourceDialog
              open={deleteDialogOpen}
              onClose={() => setDeleteDialogOpen(false)}
              requestId={id}
              module={{
                platform,
                name: moduleName,
                id: currentModuleId,
                resourceName: currentResourceName
              }}
              onSuccess={() => {
                setDeleteDialogOpen(false);
                navigate('/user/requests');
              }}
            />

            <CancelRequestDialog
              open={cancelDialogOpen}
              onClose={() => setCancelDialogOpen(false)}
              requestId={id}
              platform={platform}
              name={moduleName}
              resourceName={currentResourceName}
              onSuccess={() => {
                setCancelDialogOpen(false);
                navigate('/user/requests');
              }}
            />
          </>
        )}
      </div >
    )
  );

}

export default RegistryModule;
